<template>
  <div class="profil-page">
    <v-progress-linear v-if="loading" indeterminate color="#ff5722" class="mb-4" rounded />

    <div v-if="profil" class="profil">
      <!-- Cover hero -->
      <div class="cover">
        <div class="cover-band" />
        <div class="cover-row">
          <div class="avatar-xl">
            <img v-if="profil.PozaBase64" :src="profil.PozaBase64" class="avatar-img" alt="poza" />
            <div v-else class="avatar-ini">{{ initiale }}</div>
            <button class="cam-btn" title="Schimbă poza" @click="triggerPoza">
              <v-icon size="15" color="#fff">mdi-camera</v-icon>
            </button>
            <input ref="pozaInput" type="file" accept="image/jpeg,image/png" class="d-none" @change="onPozaSelected" />
          </div>
          <div class="cover-info">
            <h1 class="cover-name">{{ numeComplet }}</h1>
            <div class="cover-role">
              <span v-if="profil.Functie">{{ profil.Functie }}</span>
              <span v-if="profil.Functie && profil.Departament" class="sep">•</span>
              <span v-if="profil.Departament">{{ profil.Departament }}</span>
              <span v-if="!profil.Functie && !profil.Departament">Angajat</span>
            </div>
            <div class="cover-stats">
              <span v-if="profil.Marca" class="stat"><v-icon size="14" color="#ff5722" class="mr-1">mdi-badge-account-horizontal-outline</v-icon>Marcă <b class="ml-1">{{ profil.Marca }}</b></span>
              <span v-if="profil.ZileConcediu != null" class="stat"><v-icon size="14" color="#0ea5e9" class="mr-1">mdi-umbrella-beach</v-icon><b>{{ profil.ZileConcediu }}</b>&nbsp;zile concediu</span>
              <span v-if="vechimeText" class="stat"><v-icon size="14" color="#16a34a" class="mr-1">mdi-calendar-check</v-icon>Angajat din <b class="ml-1">{{ vechimeText }}</b></span>
            </div>
          </div>
          <div class="cover-actions">
            <v-btn class="btn-modif" prepend-icon="mdi-file-document-edit-outline" @click="openModif">Solicită modificare</v-btn>
          </div>
        </div>
      </div>

      <!-- Conținut: info (stânga lat) + contact/semnătură (dreapta) -->
      <div class="content">
        <main class="info-grid">
          <div v-for="s in sectiuni" :key="s.key" class="card" :class="{ wide: s.key === 'personale' }">
            <div class="card-h">
              <span class="card-ic" :style="{ background: s.soft, color: s.color }"><v-icon size="15">{{ s.icon }}</v-icon></span>
              {{ s.title }}
            </div>
            <div class="card-fields">
              <div v-for="c in s.rows" :key="c.label" class="fld">
                <div class="fld-label">{{ c.label }}</div>
                <div class="fld-value" :class="{ mono: monoField(c.label) }">{{ c.value }}</div>
              </div>
            </div>
          </div>
        </main>

        <aside class="side">
          <!-- Contact -->
          <div class="card">
            <div class="card-h">
              <span class="card-ic" style="background:#fff7ed;color:#f59e0b"><v-icon size="15">mdi-card-account-mail-outline</v-icon></span>
              Date de contact<span class="edit-hint">Editabil</span>
            </div>
            <div class="card-body">
              <p class="card-cap">Adresa de email și telefonul tău, vizibile pentru HR.</p>
              <v-form v-model="editValid" ref="editForm">
                <v-text-field v-model="editData.Email" prepend-inner-icon="mdi-email-outline" label="Email"
                  variant="outlined" density="comfortable" hide-details="auto" class="mb-3" :rules="[emailRule]" type="email" />
                <v-text-field v-model="editData.Telefon" prepend-inner-icon="mdi-phone-outline" label="Telefon"
                  variant="outlined" density="comfortable" hide-details="auto" />
              </v-form>
              <v-alert v-if="saveMsg" :type="saveMsgType" variant="tonal" density="compact" class="mt-3" closable @click:close="saveMsg = ''">{{ saveMsg }}</v-alert>
              <v-btn block class="btn-save mt-3" :loading="saveLoading" :disabled="!editValid" prepend-icon="mdi-content-save-outline" @click="salveaza">Salvează</v-btn>
            </div>
          </div>

          <!-- Semnătură -->
          <div class="card">
            <div class="card-h">
              <span class="card-ic" style="background:#f5f3ff;color:#8b5cf6"><v-icon size="15">mdi-draw-pen</v-icon></span>
              Semnătură
            </div>
            <div class="card-body">
              <!-- Vizualizare semnătură salvată -->
              <template v-if="profil.SemnaturaBase64 && !semnEditMode">
                <div class="sig-view">
                  <img :src="profil.SemnaturaBase64" alt="semnătură" />
                </div>
                <div class="sig-view-row">
                  <span class="sig-saved"><v-icon size="14" color="#16a34a" class="mr-1">mdi-check-circle</v-icon>Salvată</span>
                  <v-btn size="small" variant="text" class="btn-resign" prepend-icon="mdi-refresh" @click="startResign">Reface</v-btn>
                </div>
              </template>
              <!-- Pad de semnat -->
              <template v-else>
                <div class="sig-pad-wrap">
                  <canvas ref="sigCanvas" class="sig-canvas"
                    @pointerdown="sigDown" @pointermove="sigMove" @pointerup="sigUp" @pointerleave="sigUp" />
                  <div v-if="!sigHasDrawn" class="sig-hint"><v-icon size="15" class="mr-1">mdi-gesture</v-icon>Semnează aici</div>
                  <div class="sig-baseline" />
                </div>
                <div class="sig-actions">
                  <v-btn size="small" variant="text" :disabled="!sigHasDrawn" @click="sigClear">Șterge</v-btn>
                  <div class="d-flex align-center" style="gap:6px">
                    <v-btn v-if="profil.SemnaturaBase64" size="small" variant="text" @click="semnEditMode = false">Renunță</v-btn>
                    <v-btn size="small" class="btn-mini" :loading="semnLoading" :disabled="!sigHasDrawn" @click="sigSave">Salvează</v-btn>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <div v-else-if="!loading" class="empty-state">
      <v-icon size="42" color="#cbd5e1">mdi-account-question-outline</v-icon>
      <p>Profilul nu a putut fi încărcat.</p>
    </div>

    <!-- Dialog Solicită modificare — formular complet -->
    <v-dialog v-model="dialogModif" max-width="760" scrollable>
      <v-card rounded="xl" class="modif-card">
        <div class="dlg-head">
          <div class="dlg-head-ic"><v-icon color="#fff" size="20">mdi-file-document-edit-outline</v-icon></div>
          <div class="dlg-head-txt">
            <div class="dlg-title">Solicită modificare date</div>
            <div class="dlg-desc">Precompletat cu datele tale. Modifică ce vrei — cererea merge la HR.</div>
          </div>
          <v-btn icon="mdi-close" variant="text" color="#fff" size="small" @click="dialogModif = false" />
        </div>

        <v-card-text class="pa-0 modif-body">
          <div class="modif-inner">
            <div class="modif-toprow">
              <v-textarea v-model="modif.Mesaj" label="Mesaj pentru HR *" variant="outlined" density="comfortable" rows="2" auto-grow hide-details class="modif-msg" />
              <v-select v-model="modif.PrioritateID" :items="prioritati" item-title="Name" item-value="ID"
                label="Prioritate" variant="outlined" density="comfortable" hide-details class="modif-prio" />
            </div>

            <div class="modif-tip">
              <v-icon size="15" color="#94a3b8" class="mr-1">mdi-gesture-tap</v-icon>
              Deschide doar secțiunile pe care vrei să le corectezi.
            </div>

            <v-expansion-panels v-model="modifPanels" multiple variant="accordion" class="modif-panels">
              <v-expansion-panel value="personale">
                <v-expansion-panel-title>
                  <span class="pnl-ic" style="background:#fff1ec;color:#ff5722"><v-icon size="15">mdi-account-outline</v-icon></span>
                  <span class="pnl-title">Date personale</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="modif-grid">
                    <v-text-field v-model="modif.Name" label="Prenume" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Surname" label="Nume" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.BornDate" label="Data nașterii" type="date" variant="outlined" density="comfortable" hide-details />
                    <v-select v-model="modif.Sex" :items="sexItems" item-title="text" item-value="value" label="Sex" variant="outlined" density="comfortable" hide-details />
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel value="act">
                <v-expansion-panel-title>
                  <span class="pnl-ic" style="background:#f5f3ff;color:#8b5cf6"><v-icon size="15">mdi-card-account-details-outline</v-icon></span>
                  <span class="pnl-title">Act de identitate</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="modif-grid">
                    <v-text-field v-model="modif.DocIdentTip" label="Tip act" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.DocIdentSerie" label="Serie" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.DocIdentNo" label="Număr" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Cnp" label="CNP" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.DocIdentIssuer" label="Emitent" variant="outlined" density="comfortable" hide-details />
                    <v-select v-model="modif.MrtStatus" :items="mrtItems" label="Stare civilă" variant="outlined" density="comfortable" hide-details clearable />
                    <v-text-field v-model="modif.DocIdentIssueDate" label="Data eliberării" type="date" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.DocIdentExpiryDate" label="Data expirării" type="date" variant="outlined" density="comfortable" hide-details />
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel value="adresa">
                <v-expansion-panel-title>
                  <span class="pnl-ic" style="background:#fef2f2;color:#ef4444"><v-icon size="15">mdi-map-marker-outline</v-icon></span>
                  <span class="pnl-title">Adresă & contact</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="modif-grid">
                    <v-text-field v-model="modif.Street" label="Stradă" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.StreetNr" label="Nr." variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Block" label="Bloc" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Entrance" label="Scară" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Appartment" label="Apartament" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.CP" label="Cod poștal" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Phone" label="Telefon" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.Email" label="Email" variant="outlined" density="comfortable" hide-details />
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel value="bancar">
                <v-expansion-panel-title>
                  <span class="pnl-ic" style="background:#ecfdf5;color:#16a34a"><v-icon size="15">mdi-bank-outline</v-icon></span>
                  <span class="pnl-title">Date bancare & permis auto</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="modif-grid">
                    <v-select v-model="modif.CardBankID" :items="banci" item-title="Nume" item-value="ID"
                      label="Bancă" variant="outlined" density="comfortable" hide-details clearable />
                    <v-text-field v-model="modif.CardAccNo" label="IBAN" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.CardNo" label="Nr. card" variant="outlined" density="comfortable" hide-details />
                    <v-text-field v-model="modif.DrvLicense" label="Permis auto" variant="outlined" density="comfortable" hide-details />
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-alert v-if="modifMsg" :type="modifMsgType" variant="tonal" density="compact" class="mt-4">{{ modifMsg }}</v-alert>
          </div>
        </v-card-text>

        <v-card-actions class="modif-foot">
          <v-btn variant="text" @click="dialogModif = false">Închide</v-btn>
          <v-spacer />
          <v-btn class="btn-modif px-6" :loading="modifLoading" :disabled="!modif.Mesaj" @click="trimiteModif">
            <v-icon start>mdi-send</v-icon>Trimite cererea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PORTAL_Profil from '@/services/Portal/PORTAL_Profil'

export default defineComponent({
  name: 'ProfilAngajat',
  data() {
    return {
      loading: false,
      saveLoading: false,
      editValid: false,
      profil: null as any,
      editData: { Email: '', Telefon: '' },
      saveMsg: '',
      saveMsgType: 'success' as 'success' | 'error',
      // semnatura
      semnLoading: false,
      drawing: false,
      sigHasDrawn: false,
      semnEditMode: false,
      // dialog modificare
      dialogModif: false,
      modifLoading: false,
      modifMsg: '',
      modifMsgType: 'success' as 'success' | 'error',
      prioritati: [] as any[],
      banci: [] as any[],
      tari: [] as any[],
      judete: [] as any[],
      localitati: [] as any[],
      sexItems: [{ text: 'Masculin', value: 'M' }, { text: 'Feminin', value: 'F' }],
      mrtItems: ['Casatorit', 'Necasatorit'],
      modifPanels: [] as string[],
      modif: {
        Mesaj: '', PrioritateID: null as string | null,
        Name: '', Surname: '', BornDate: '', Sex: '',
        DocIdentTip: '', DocIdentSerie: '', DocIdentNo: '', Cnp: '', DocIdentIssuer: '',
        DocIdentIssueDate: '', DocIdentExpiryDate: '', MrtStatus: '',
        DrvLicense: '',
        CardBankID: null as string | null, CardAccNo: '', CardNo: '',
        CountryID: null as string | null, DistrictID: null as string | null, CityID: null as string | null,
        Street: '', StreetNr: '', Block: '', Entrance: '', Appartment: '', CP: '',
        Phone: '', Email: '',
      },
    }
  },
  computed: {
    numeComplet(): string {
      const p = this.profil || {};
      return [p.Nume, p.Prenume].filter(Boolean).join(' ') || 'Angajat';
    },
    initiale(): string {
      const p = this.profil || {};
      const ini = `${(p.Nume || '').charAt(0)}${(p.Prenume || '').charAt(0)}`.toUpperCase();
      return ini || 'AN';
    },
    vechimeText(): string {
      const p = this.profil || {};
      const d = p.DataAngajare ?? p.DataStartVechimeFirma;
      if (!d) return '';
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' });
    },
    sectiuni(): any[] {
      const p = this.profil || {};
      const defs = [
        {
          key: 'personale', title: 'Date personale', icon: 'mdi-account-outline', color: '#ff5722', soft: '#fff1ec',
          rows: this.rows([
            ['Nume', p.Nume], ['Prenume', p.Prenume], ['CNP', p.Cnp],
            ['Data nașterii', this.fmtDate(p.DataNasterii)], ['Locul nașterii', p.LoculNasterii],
            ['Sex', this.sex(p.Sex)], ['Naționalitate', p.Nationalitate], ['Meserie', p.Meserie],
          ]),
        },
        {
          key: 'act', title: 'Act de identitate', icon: 'mdi-card-account-details-outline', color: '#8b5cf6', soft: '#f5f3ff',
          rows: this.rows([
            ['Tip act', p.ActTip], ['Serie', p.ActSerie], ['Număr', p.ActNumar], ['Emitent', p.ActEmitent],
            ['Data eliberării', this.fmtDate(p.ActDataEliberare)], ['Data expirării', this.fmtDate(p.ActDataExpirare)],
          ]),
        },
        {
          key: 'contract', title: 'Contract & angajare', icon: 'mdi-briefcase-outline', color: '#0ea5e9', soft: '#eff6ff',
          rows: this.rows([
            ['Funcție', p.Functie], ['Departament', p.Departament], ['Data angajării', this.fmtDate(p.DataAngajare)],
            ['Vechime firmă din', this.fmtDate(p.DataStartVechimeFirma)], ['Expirare contract', this.fmtDate(p.DataExpiraContract)],
            ['Ore contract', p.OreContract != null ? String(p.OreContract) : null],
            ['Perioadă probă', p.PerioadaProba == null ? null : (p.PerioadaProba ? 'Da' : 'Nu')],
            ['Finalizare probă', this.fmtDate(p.DataFinalizareProba)],
          ]),
        },
        {
          key: 'adresa', title: 'Adresă', icon: 'mdi-map-marker-outline', color: '#ef4444', soft: '#fef2f2',
          rows: this.rows([
            ['Țară', p.Tara], ['Județ', p.Judet], ['Localitate', p.Localitate],
            ['Stradă', p.Strada], ['Număr', p.StradaNr], ['Bloc', p.Bloc],
            ['Scară', p.Scara], ['Apartament', p.Apartament], ['Cod poștal', p.CodPostal],
          ]),
        },
        {
          key: 'bancar', title: 'Date bancare', icon: 'mdi-bank-outline', color: '#16a34a', soft: '#ecfdf5',
          rows: this.rows([
            ['Bancă', p.Banca], ['Cont / IBAN', p.ContIban], ['Nr. card', p.NrCard],
          ]),
        },
        {
          key: 'concediu', title: 'Concediu', icon: 'mdi-beach', color: '#0ea5e9', soft: '#eff6ff',
          rows: this.rows([
            ['Zile concediu', p.ZileConcediu != null ? `${p.ZileConcediu} Zile` : null],
            ['Permis auto', p.DrvLicense],
          ]),
        },
      ];
      return defs.filter(s => s.rows.length);
    },
  },
  async mounted() {
    this.loading = true;
    try {
      const resp = await PORTAL_Profil.get();
      this.profil = resp.data;
      this.editData.Email = this.profil?.Email ?? '';
      this.editData.Telefon = this.profil?.Telefon ?? '';
      // Inițializăm pad-ul doar dacă nu există deja o semnătură salvată (altfel afișăm imaginea).
      if (!this.profil?.SemnaturaBase64) this.$nextTick(() => this.initCanvas());
    } finally {
      this.loading = false;
    }
    try {
      const [prio, banci, tari] = await Promise.all([
        PORTAL_Profil.getPrioritati(), PORTAL_Profil.getBanci(), PORTAL_Profil.getTari(),
      ]);
      this.prioritati = prio.data ?? [];
      this.banci = banci.data ?? [];
      this.tari = tari.data ?? [];
    } catch { /* dropdown-urile rămân goale */ }
  },
  methods: {
    rows(pairs: [string, any][]): { label: string; value: string }[] {
      return pairs
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([label, value]) => ({ label, value: String(value) }));
    },
    fmtDate(d: any): string | null {
      if (!d) return null;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return null;
      return dt.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    sex(s: any): string | null {
      if (!s) return null;
      const v = String(s).toUpperCase();
      if (v === 'M') return 'Masculin';
      if (v === 'F') return 'Feminin';
      return String(s);
    },
    emailRule(v: string) { return !v || /.+@.+\..+/.test(v) || 'Email invalid'; },
    monoField(label: string) { return ['CNP', 'Cont / IBAN', 'Nr. card', 'Număr'].includes(label); },

    // ---- Poză ----
    triggerPoza() { (this.$refs.pozaInput as HTMLInputElement)?.click(); },
    onPozaSelected(e: Event) {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = String(reader.result);
        try {
          await PORTAL_Profil.uploadPoza(b64);
          if (this.profil) this.profil.PozaBase64 = b64;
        } catch { /* ignore */ }
      };
      reader.readAsDataURL(file);
    },

    // ---- Semnătură (canvas) ----
    initCanvas() {
      const c = this.$refs.sigCanvas as HTMLCanvasElement;
      if (!c || !c.offsetWidth) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      c.width = c.offsetWidth * ratio;
      c.height = c.offsetHeight * ratio;
      const ctx = c.getContext('2d')!;
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1e293b';
      this.sigHasDrawn = false;
    },
    sigPos(e: PointerEvent) {
      const c = this.$refs.sigCanvas as HTMLCanvasElement;
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    },
    sigDown(e: PointerEvent) {
      this.drawing = true;
      const c = this.$refs.sigCanvas as HTMLCanvasElement;
      c.setPointerCapture?.(e.pointerId);
      const ctx = c.getContext('2d')!;
      const p = this.sigPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
    },
    sigMove(e: PointerEvent) {
      if (!this.drawing) return;
      const ctx = (this.$refs.sigCanvas as HTMLCanvasElement).getContext('2d')!;
      const p = this.sigPos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
      this.sigHasDrawn = true;
    },
    sigUp() { this.drawing = false; },
    sigClear() {
      const c = this.$refs.sigCanvas as HTMLCanvasElement;
      c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
      this.sigHasDrawn = false;
    },
    async sigSave() {
      if (!this.sigHasDrawn) return;
      const c = this.$refs.sigCanvas as HTMLCanvasElement;
      const b64 = c.toDataURL('image/png');
      this.semnLoading = true;
      try {
        await PORTAL_Profil.saveSemnatura(b64);
        if (this.profil) this.profil.SemnaturaBase64 = b64;
        this.semnEditMode = false;
      } catch { /* ignore */ } finally { this.semnLoading = false; }
    },
    startResign() {
      this.semnEditMode = true;
      this.sigHasDrawn = false;
      this.$nextTick(() => this.initCanvas());
    },

    // ---- Solicită modificare: prepopulare + deschidere ----
    async openModif() {
      const p = this.profil || {};
      const iso = (d: any): string => {
        if (!d) return '';
        const dt = new Date(d);
        return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
      };
      const def = this.prioritati.find((x: any) => x.DefaultValue)?.ID ?? null;
      this.modif = {
        Mesaj: '', PrioritateID: def,
        Name: p.Prenume ?? '', Surname: p.Nume ?? '', BornDate: iso(p.DataNasterii), Sex: p.Sex ?? '',
        DocIdentTip: p.ActTip ?? '', DocIdentSerie: p.ActSerie ?? '', DocIdentNo: p.ActNumar ?? '',
        Cnp: p.Cnp ?? '', DocIdentIssuer: p.ActEmitent ?? '',
        DocIdentIssueDate: iso(p.ActDataEliberare), DocIdentExpiryDate: iso(p.ActDataExpirare),
        MrtStatus: p.StareCivila ?? '',
        DrvLicense: p.DrvLicense ?? '',
        CardBankID: p.CardBankId ?? null, CardAccNo: p.ContIban ?? '', CardNo: p.NrCard ?? '',
        CountryID: p.TaraID ?? null, DistrictID: p.JudetID ?? null, CityID: p.LocalitateID ?? null,
        Street: p.Strada ?? '', StreetNr: p.StradaNr ?? '', Block: p.Bloc ?? '',
        Entrance: p.Scara ?? '', Appartment: p.Apartament ?? '', CP: p.CodPostal ?? '',
        Phone: p.Telefon ?? '', Email: p.Email ?? '',
      };
      this.judete = [];
      this.localitati = [];
      this.modifMsg = '';
      this.dialogModif = true;
      // Încarcă județele/localitățile aferente valorilor prepopulate (fără a le reseta).
      if (p.TaraID) {
        try { this.judete = (await PORTAL_Profil.getJudete(p.TaraID)).data ?? []; } catch { /* */ }
      }
      if (p.JudetID) {
        try { this.localitati = (await PORTAL_Profil.getLocalitati(p.JudetID)).data ?? []; } catch { /* */ }
      }
    },
    async onTaraChange(taraId: string | null) {
      this.modif.DistrictID = null;
      this.modif.CityID = null;
      this.judete = [];
      this.localitati = [];
      if (!taraId) return;
      try { this.judete = (await PORTAL_Profil.getJudete(taraId)).data ?? []; } catch { /* */ }
    },
    async onJudetChange(judetId: string | null) {
      this.modif.CityID = null;
      this.localitati = [];
      if (!judetId) return;
      try { this.localitati = (await PORTAL_Profil.getLocalitati(judetId)).data ?? []; } catch { /* */ }
    },

    // ---- Contact ----
    async salveaza() {
      this.saveLoading = true;
      this.saveMsg = '';
      try {
        await PORTAL_Profil.update({ Email: this.editData.Email, Telefon: this.editData.Telefon });
        if (this.profil) { this.profil.Email = this.editData.Email; this.profil.Telefon = this.editData.Telefon; }
        this.saveMsg = 'Datele de contact au fost salvate.';
        this.saveMsgType = 'success';
      } catch {
        this.saveMsg = 'Eroare la salvare. Încearcă din nou.';
        this.saveMsgType = 'error';
      } finally {
        this.saveLoading = false;
      }
    },

    // ---- Solicită modificare ----
    async trimiteModif() {
      this.modifLoading = true;
      this.modifMsg = '';
      try {
        const payload: any = {};
        Object.entries(this.modif).forEach(([k, v]) => { payload[k] = v === '' ? null : v; });
        const resp = await PORTAL_Profil.solicitaModificare(payload);
        this.modifMsg = `Cerere trimisă (${resp.data?.TicketNr ?? 'OK'}). HR o va procesa.`;
        this.modifMsgType = 'success';
        setTimeout(() => { this.dialogModif = false; this.modifMsg = ''; }, 1600);
      } catch {
        this.modifMsg = 'Eroare la trimiterea cererii.';
        this.modifMsgType = 'error';
      } finally {
        this.modifLoading = false;
      }
    },
  },
})
</script>

<style scoped>
.profil-page { padding: 16px; width: 100%; background: #eef1f5; min-height: 100%; }
.profil { display: flex; flex-direction: column; gap: 14px; }

/* Cover hero */
.cover { background: #fff; border: 1px solid #e9edf3; border-radius: 16px; overflow: hidden; box-shadow: 0 1px 3px rgba(15,23,42,.05); }
.cover-band { position: relative; height: 104px; overflow: hidden; background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%); }
.cover-band::before { content: ""; position: absolute; width: 260px; height: 260px; border-radius: 50%; background: rgba(255,255,255,.10); top: -140px; right: 60px; }
.cover-band::after { content: ""; position: absolute; width: 160px; height: 160px; border-radius: 50%; background: rgba(255,255,255,.08); bottom: -110px; right: 280px; }
.cover-row { display: flex; align-items: flex-end; gap: 20px; padding: 14px 26px 20px; flex-wrap: wrap; }
.avatar-xl { position: relative; width: 96px; height: 96px; flex-shrink: 0; margin-top: -64px; }
.avatar-xl .avatar-img, .avatar-ini { width: 96px; height: 96px; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 6px 18px rgba(255,87,34,.28); }
.avatar-xl .avatar-img { object-fit: cover; }
.avatar-ini { background: linear-gradient(140deg, #ff5722, #ff8a5c); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 32px; font-weight: 800; }
.cam-btn { position: absolute; right: 2px; bottom: 2px; width: 28px; height: 28px; border-radius: 50%; background: #1e293b; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.cover-info { flex: 1; min-width: 0; padding-bottom: 2px; }
.cover-name { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1.15; margin: 0; }
.cover-role { font-size: 13px; color: #64748b; font-weight: 600; margin-top: 3px; }
.cover-role .sep { margin: 0 6px; opacity: .5; }
.cover-stats { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.stat { display: inline-flex; align-items: center; font-size: 12px; font-weight: 600; color: #475569; background: #f4f6fa; border: 1px solid #eef1f6; padding: 5px 11px; border-radius: 20px; }
.cover-actions { padding-bottom: 4px; }

/* Content: info (stânga) + side (dreapta) */
.content { display: grid; grid-template-columns: 1fr 320px; gap: 14px; align-items: start; }
@media (max-width: 1000px) { .content { grid-template-columns: 1fr; } }
.info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; align-items: start; }
@media (max-width: 640px) { .info-grid { grid-template-columns: 1fr; } }
.side { display: flex; flex-direction: column; gap: 14px; }

/* Card */
.card { background: #fff; border: 1px solid #e9edf3; border-radius: 14px; box-shadow: 0 1px 3px rgba(15,23,42,.05); overflow: hidden; transition: box-shadow .15s; }
.card:hover { box-shadow: 0 6px 20px rgba(15,23,42,.07); }
.card.wide { grid-column: 1 / -1; }
.card-h { display: flex; align-items: center; gap: 10px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: .6px; color: #334155; padding: 13px 18px; background: #fbfcfe; border-bottom: 1px solid #f1f5f9; }
.card-ic { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 8px; }
.card-body { padding: 16px 18px; }
.card-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 22px; padding: 18px; }
.card.wide .card-fields { grid-template-columns: repeat(4, minmax(0, 1fr)); }
@media (max-width: 1000px) { .card.wide .card-fields { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 640px) { .card-fields, .card.wide .card-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

.edit-hint { margin-left: auto; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; color: #f59e0b; background: #fff7ed; padding: 2px 8px; border-radius: 14px; }
.card-cap { font-size: 12px; color: #94a3b8; margin: 0 0 14px; line-height: 1.4; }

/* Semnătură — vizualizare salvată */
.sig-view { border: 1px solid #eef1f6; border-radius: 12px; background: #fafbfd; padding: 10px; display: flex; align-items: center; justify-content: center; min-height: 92px; }
.sig-view img { max-height: 80px; max-width: 100%; }
.sig-view-row { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.sig-saved { display: inline-flex; align-items: center; font-size: 12px; font-weight: 700; color: #16a34a; }
.btn-resign { color: #8b5cf6 !important; font-weight: 700; text-transform: none; }

/* Semnătură — pad */
.sig-pad-wrap { position: relative; }
.sig-canvas { width: 100%; height: 100px; border: 1.5px dashed #cbd5e1; border-radius: 12px; background:
  repeating-linear-gradient(#fff, #fff 24px, #f4f6fa 24px, #f4f6fa 25px); touch-action: none; cursor: crosshair; display: block; }
.sig-hint { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 2px; color: #cbd5e1; font-size: 13px; font-weight: 600; pointer-events: none; }
.sig-baseline { position: absolute; left: 14px; right: 14px; bottom: 26px; border-bottom: 1.5px solid #e2e8f0; pointer-events: none; }
.sig-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }

.btn-modif { background: linear-gradient(120deg,#ff5722,#ff8a5c) !important; color: #fff !important; font-weight: 700; border-radius: 10px; text-transform: none; letter-spacing: 0; box-shadow: 0 4px 12px rgba(255,87,34,.3); }
.btn-save { background: #ff5722 !important; color: #fff !important; font-weight: 700; border-radius: 10px; text-transform: none; letter-spacing: 0; box-shadow: none; }
.btn-mini { background: #8b5cf6 !important; color: #fff !important; font-weight: 700; border-radius: 8px; text-transform: none; }

.fld { min-width: 0; }
.fld-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; color: #94a3b8; margin-bottom: 3px; }
.fld-value { font-size: 14px; font-weight: 600; color: #1e293b; word-break: break-word; line-height: 1.35; }
.fld-value.mono { font-family: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700; letter-spacing: .3px; color: #0f172a; }

/* Dialog */
.modif-card { overflow: hidden; }
.dlg-head { display: flex; align-items: center; gap: 12px; background: linear-gradient(120deg,#ff5722,#ff8a5c); color: #fff; padding: 16px 18px; }
.dlg-head-ic { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,.2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.dlg-head-txt { flex: 1; min-width: 0; }
.dlg-title { font-size: 16px; font-weight: 800; line-height: 1.15; }
.dlg-desc { font-size: 12px; opacity: .9; margin-top: 2px; }
.modif-body { max-height: 74vh; background: #f4f6fa; }
.modif-inner { padding: 18px 20px 6px; }
.modif-toprow { display: grid; grid-template-columns: 1fr 240px; gap: 14px; align-items: start; margin-bottom: 12px; }
@media (max-width: 620px) { .modif-toprow { grid-template-columns: 1fr; } }
.modif-msg :deep(textarea), .modif-toprow :deep(.v-field) { background: #fff; }
.modif-tip { display: flex; align-items: center; font-size: 12px; color: #94a3b8; margin: 0 2px 12px; }

/* Accordion */
.modif-panels { border-radius: 12px; overflow: hidden; }
.modif-panels :deep(.v-expansion-panel) { background: #fff; margin-bottom: 10px; border: 1px solid #e9edf3; border-radius: 12px !important; box-shadow: 0 1px 3px rgba(15,23,42,.04); }
.modif-panels :deep(.v-expansion-panel::after) { display: none; }
.modif-panels :deep(.v-expansion-panel-title) { min-height: 52px; font-weight: 700; color: #1e293b; }
.pnl-ic { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 9px; margin-right: 11px; }
.pnl-title { font-size: 13.5px; font-weight: 700; }
.modif-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 14px; padding-top: 6px; }
@media (max-width: 560px) { .modif-grid { grid-template-columns: 1fr; } }
.modif-foot { padding: 14px 20px; background: #fff; border-top: 1px solid #eef1f6; }

.ms-sec { background: #fff; border: 1px solid #eef1f6; border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(15,23,42,.04); }
.ms-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 14px; }
.ms-ic { display: inline-flex; align-items: center; justify-content: center; width: 25px; height: 25px; border-radius: 8px; }

.modif-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 14px; }
@media (max-width: 560px) { .modif-grid { grid-template-columns: 1fr; } }

.empty-state { text-align: center; padding: 48px 0; color: #94a3b8; }
.empty-state p { margin-top: 8px; font-size: 13px; }
</style>
